import React, { useContext, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Language } from "../../App";

/**
 * SEO & Structured Metadata component for the Courses page.
 * Manages dynamic document title, meta tags (OpenGraph, Twitter, App Links, Robots),
 * and Schema.org JSON-LD structured data for rich search engine indexing.
 */
const CoursesSEO = ({ selectedCategoryName = null, selectedCourseType = null }) => {
  const { lang } = useContext(Language) || {};

  const isSpanish = useMemo(() => {
    try {
      if (typeof window !== "undefined") {
        const searchParams = new URLSearchParams(window.location.search);
        const urlLang = searchParams.get("lang");
        if (urlLang) {
          const lower = urlLang.toLowerCase().trim();
          if (lower === "es" || lower === "spanish" || lower === "sp") return true;
          if (lower === "en" || lower === "english") return false;
        }
        if (searchParams.has("es") || searchParams.has("spanish")) return true;
        if (window.location.pathname.toLowerCase().includes("/es")) return true;
      }
      const stored = localStorage.getItem("selectedLanguage");
      if (stored === "Spanish") return true;
      if (stored === "English") return false;
      return (
        lang?.code === "es" ||
        (typeof navigator !== "undefined" &&
          Boolean(navigator.language?.toLowerCase()?.startsWith("es")))
      );
    } catch {
      return false;
    }
  }, [lang]);

  // Compute canonical URL & origin safely
  const { siteOrigin, canonicalUrl, pageUrl } = useMemo(() => {
    const origin =
      typeof window !== "undefined" && window.location.origin
        ? window.location.origin
        : "https://ophthalmologyradar.com";

    const path = isSpanish ? "/courses/es" : "/courses";
    const params = new URLSearchParams();

    if (selectedCourseType && selectedCourseType !== "all") {
      params.set("type", selectedCourseType);
    }

    const queryString = params.toString();
    const fullUrl = queryString ? `${origin}${path}?${queryString}` : `${origin}${path}`;
    const cleanCanonical = `${origin}${path}`;

    return {
      siteOrigin: origin,
      canonicalUrl: cleanCanonical,
      pageUrl: fullUrl,
    };
  }, [selectedCourseType, isSpanish]);

  // Default social share image (1200x630 Courses Banner)
  const ogImage = useMemo(() => {
    const origin =
      typeof window !== "undefined" && window.location.origin
        ? window.location.origin
        : "https://ophthalmologyradar.com";

    return isSpanish ? `${origin}/courses-banner-es.png` : `${origin}/courses-banner.png`;
  }, [isSpanish]);

  // Dynamic Title
  const title = useMemo(() => {
    if (selectedCategoryName) {
      return isSpanish
        ? `Cursos: ${selectedCategoryName} | Ophthalmology Radar`
        : `Courses: ${selectedCategoryName} | Ophthalmology Radar`;
    }

    if (selectedCourseType === "purchased") {
      return isSpanish
        ? "Mis Cursos | Ophthalmology Radar"
        : "Purchased Courses | Ophthalmology Radar";
    }

    if (selectedCourseType === "saved") {
      return isSpanish
        ? "Cursos Guardados | Ophthalmology Radar"
        : "Saved Courses | Ophthalmology Radar";
    }

    return isSpanish
      ? "Cursos | Ophthalmology Radar"
      : "Courses | Ophthalmology Radar";
  }, [selectedCategoryName, selectedCourseType, isSpanish]);

  // Dynamic Description
  const description = useMemo(() => {
    if (selectedCategoryName) {
      return isSpanish
        ? `Cursos quirúrgicos y clases magistrales de ${selectedCategoryName} en Ophthalmology Radar.`
        : `Surgical courses and masterclasses in ${selectedCategoryName} on Ophthalmology Radar.`;
    }

    return isSpanish
      ? "Explora cursos quirúrgicos acreditados, clases magistrales y formación médica continua para oftalmólogos en Ophthalmology Radar."
      : "Explore accredited surgical courses, masterclasses, and continuing medical education for ophthalmologists on Ophthalmology Radar.";
  }, [selectedCategoryName, isSpanish]);

  // Dynamic Keywords
  const keywords = useMemo(() => {
    return isSpanish
      ? "cursos de oftalmología, clases magistrales de cirugía, facoemulsificación, cirugía de retina vitrectomía, cirugía de glaucoma MIGS, córnea y refractiva, créditos CME, educación médica continua, certificado médico, Ophthalmology Radar"
      : "ophthalmology courses, ophthalmic surgical masterclasses, cataract phacoemulsification, vitreoretinal surgery, MIGS glaucoma surgery, cornea refractive surgery, CME credits, medical video lectures, doctor certificates, Ophthalmology Radar";
  }, [isSpanish]);

  // Schema.org Structured Data (Course Collection & Educational Occupational Credential)
  const schemaJson = useMemo(() => {
    const schemaObj = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      description: description,
      url: pageUrl,
      inLanguage: isSpanish ? "es-ES" : "en-US",
      isPartOf: {
        "@type": "WebSite",
        name: "Ophthalmology Radar",
        url: siteOrigin,
      },
      about: [
        {
          "@type": "MedicalSpecialty",
          name: "Ophthalmology",
        },
        {
          "@type": "Thing",
          name: "Continuing Medical Education",
        },
        {
          "@type": "Thing",
          name: "Ophthalmic Surgery Masterclasses",
        },
      ],
      mainEntity: {
        "@type": "ItemList",
        name: title,
        description: description,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            item: {
              "@type": "Course",
              name: isSpanish
                ? "Facoemulsificación Avanzada y LIO Premium"
                : "Advanced Phacoemulsification & Premium IOL Implantation",
              description: isSpanish
                ? "Técnicas avanzadas para cataratas complejas, soporte capsular y alineación tórica."
                : "Advanced techniques for dense cataracts, capsular support, and toric alignment.",
              provider: {
                "@type": "Organization",
                name: "Ophthalmology Radar",
                url: siteOrigin,
              },
              educationalCredentialAwarded: "3.5 CME Credits",
            },
          },
          {
            "@type": "ListItem",
            position: 2,
            item: {
              "@type": "Course",
              name: isSpanish
                ? "Cirugía de Glaucoma MIGS y Control de PIO"
                : "Minimally Invasive Glaucoma Surgery (MIGS) Protocols",
              description: isSpanish
                ? "Abordajes microinvasivos, implantes trabeculares y reducción de presión intraocular."
                : "Micro-invasive bypass, canaloplasty, and surgical intraocular pressure control.",
              provider: {
                "@type": "Organization",
                name: "Ophthalmology Radar",
                url: siteOrigin,
              },
              educationalCredentialAwarded: "2.5 CME Credits",
            },
          },
        ],
      },
    };

    return JSON.stringify(schemaObj);
  }, [title, description, pageUrl, siteOrigin, isSpanish]);

  return (
    <Helmet>
      {/* Primary HTML Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="robots"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <meta name="language" content={isSpanish ? "Spanish" : "English"} />

      {/* Multilingual Alternate Links (hreflang for international SEO) */}
      <link rel="alternate" hrefLang="en" href={`${siteOrigin}/courses`} />
      <link rel="alternate" hrefLang="es" href={`${siteOrigin}/courses/es`} />
      <link rel="alternate" hrefLang="x-default" href={`${siteOrigin}/courses`} />

      {/* Open Graph / Facebook / LinkedIn / WhatsApp */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Ophthalmology Radar" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content={
          isSpanish
            ? "Cursos Quirúrgicos y Clases Magistrales | Ophthalmology Radar"
            : "Surgical Courses & Accredited CME Masterclasses | Ophthalmology Radar"
        }
      />
      <meta property="og:locale" content={isSpanish ? "es_ES" : "en_US"} />
      <meta property="og:locale:alternate" content={isSpanish ? "en_US" : "es_ES"} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@MedicalRadar" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta
        name="twitter:image:alt"
        content={
          isSpanish
            ? "Cursos Quirúrgicos y Clases Magistrales | Ophthalmology Radar"
            : "Surgical Courses & Accredited CME Masterclasses | Ophthalmology Radar"
        }
      />

      {/* Deep Linking App Metadata */}
      <meta property="al:ios:app_name" content="Medicalradar" />
      <meta property="al:ios:app_store_id" content="1605786600" />
      <meta property="al:ios:url" content={pageUrl} />
      <meta property="al:android:app_name" content="MedicalRadar" />
      <meta
        property="al:android:package"
        content="com.drudotstech.medicalradar"
      />
      <meta property="al:android:url" content={pageUrl} />

      {/* Schema.org Structured Data */}
      <script type="application/ld+json">{schemaJson}</script>
    </Helmet>
  );
};

export default React.memo(CoursesSEO);
